import {
  Company,
  DisplayUserType,
  FormattedDisplayUserOptions,
  Role,
  User,
} from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';

import {
  ASSIGNEE_PICKER_COMPANY,
  ASSIGNEE_PICKER_ROLE,
  ASSIGNEE_PICKER_USER,
} from '../consts';
import {
  AdditionalEntityDataFields,
  Status,
  EntityOptionsFilter,
  ItemWithGroup,
  StringValueKeys,
} from '../types';

export const transformRoles = (
  roles: Role[],
  valueGetter: StringValueKeys<Role>,
  T: ReturnType<typeof useLocale>['T'],
  statusFilters?: Status[],
  roleOptionsFilter?: EntityOptionsFilter<Role>,
  additionalDataFields?: AdditionalEntityDataFields<Role>,
  rolesMemberCount: Record<string, number> = {},
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
) => {
  return postTransform(
    roles.reduce<ItemWithGroup[]>((result, role, i) => {
      const isStatusVisible =
        !statusFilters || statusFilters.includes(role.status);

      const isOptionVisible =
        !roleOptionsFilter ||
        roleOptionsFilter(role, i, roles, rolesMemberCount);

      if (isStatusVisible && isOptionVisible) {
        result.push({
          ...additionalDataFields?.reduce(
            (a, v) => ({ ...a, [v]: role[v] }),
            {},
          ),
          label: role.name,
          value: role[valueGetter],
          subtext: T.translate('MEMBERS_COUNT', {
            context: rolesMemberCount[role.id] || 0,
          }) as string,
          group: ASSIGNEE_PICKER_ROLE,
          memberCount: rolesMemberCount[role.id],
        });
      }

      return result;
    }, []),
  );
};

export const transformUsers = (
  users: User[],
  valueGetter: StringValueKeys<User>,
  formatDisplayUser: (
    userId: string,
    options?: FormattedDisplayUserOptions | undefined,
  ) => DisplayUserType,
  statusFilters?: Status[],
  userOptionsFilter?: EntityOptionsFilter<User>,
  additionalDataFields?: AdditionalEntityDataFields<User>,
  idToNameMap: Record<string, string> = {},
  userOptionSubtextRenderer?: (user: User) => string,
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
  hideDeletedEntitiesInMenu?: boolean,
): [Record<string, number>, ItemWithGroup[]] => {
  const rolesCountMap: { [id: string]: number } = {};

  const options = postTransform(
    users.reduce<ItemWithGroup[]>((result, user, i) => {
      const isStatusVisible =
        !statusFilters || statusFilters.includes(user.status);

      const isOptionVisible =
        !userOptionsFilter || userOptionsFilter(user, i, users);

      user.roleIds?.forEach((roleId) => {
        rolesCountMap[roleId] = (rolesCountMap[roleId] || 0) + 1;
      });

      if (isStatusVisible && isOptionVisible && user.autodeskId) {
        result.push({
          ...additionalDataFields?.reduce(
            (a, v) => ({ ...a, [v]: user[v] }),
            {},
          ),
          label:
            formatDisplayUser(user.autodeskId).displayName ||
            user.name ||
            user.email,
          value: user[valueGetter],
          subtext: userOptionSubtextRenderer
            ? userOptionSubtextRenderer(user)
            : idToNameMap?.[user?.companyId] || '',
          group: ASSIGNEE_PICKER_USER,
          imageUrl: user?.imageUrl,
          hidden: hideDeletedEntitiesInMenu
            ? user.status === 'deleted'
            : undefined,
        });
      }

      return result;
    }, []),
  );

  return [rolesCountMap, options];
};

export const transformCompanies = (
  companies: Company[],
  valueGetter: StringValueKeys<Company>,
  T: ReturnType<typeof useLocale>['T'],
  statusFilters?: Status[],
  companyOptionsFilter?: EntityOptionsFilter<Company>,
  additionalDataFields?: AdditionalEntityDataFields<Company>,
  postTransform: (
    originalTransformResult: ItemWithGroup[],
  ) => ItemWithGroup[] = (result) => result,
): [Record<string, string>, ItemWithGroup[]] => {
  const idToNameMap: { [id: string]: string } = {};
  const options = postTransform(
    companies.reduce<ItemWithGroup[]>((result, company, i) => {
      const isStatusVisible =
        !statusFilters ||
        (company.status && statusFilters.includes(company.status));

      const isOptionVisible =
        !companyOptionsFilter || companyOptionsFilter(company, i, companies);

      if (isStatusVisible) {
        idToNameMap[company.id] = company.name;

        if (isOptionVisible) {
          result.push({
            ...additionalDataFields?.reduce(
              (a, v) => ({ ...a, [v]: company[v] }),
              {},
            ),
            label: company.name,
            value: company[valueGetter],
            group: ASSIGNEE_PICKER_COMPANY,
            subtext: T.translate('MEMBERS_COUNT', {
              context: company.memberCount,
            }) as string,
            imageUrl: company?.imageUrl,
            memberCount: company.memberCount,
          });
        }
      }

      return result;
    }, []),
  );

  return [idToNameMap, options];
};
